/*
    © Copyright LinkHMS B.V. All rights reserved.
    * Proprietary and confidential.
    * Unauthorized copying of this file, via any medium is strictly prohibited.
*/
import { inject, Injectable, isDevMode } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { StoreService } from '@shared/services/store.service';
import { filter, interval, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly logPrefix = '[AppUpdate]';
  private readonly USER_RELOAD_REJECT_COUNT_STORAGE_KEY = 'versionUpdateRejectedTimes';
  private readonly UPDATE_CHECK_INTERVAL = 900000; // 15 minutes in ms
  private readonly CONFIRMATION_RELOAD_INTERVAL = 300000; // 5 minutes in ms
  private readonly USER_CAN_REJECT_UPDATE_MAX_COUNT = 5;

  private readonly storeService = inject(StoreService);
  private readonly translate = inject(TranslateService);
  private readonly updates = inject(SwUpdate);
  private started = false;
  private confirmReloadIntervalTimeout: any | null = null;

  private log(message: string, reason?: any): void {
    console.log(`${this.logPrefix}: ${message}`, reason ?? '');
  }

  start(): void {
    if (!this.updates.isEnabled || this.started) {
      this.log(`Service disabled. Is dev mode: ${isDevMode()}`);
      return;
    }

    this.started = true;

    this.log(
      `Enable update checker. Approx interval: ${this.UPDATE_CHECK_INTERVAL / 60000}min.` +
        ` Is dev mode: ${isDevMode()}`,
    );

    this.subscribeToUnrecoverableState();
    this.subscribeToVersionChanges();

    interval(this.UPDATE_CHECK_INTERVAL).subscribe(() => {
      this.log('Executing "update verification" request');
      this.executeUpdateVerification();
    });
  }

  private subscribeToVersionChanges() {
    this.updates.versionUpdates
      .pipe(
        tap((e) => {
          switch (e.type) {
            case 'VERSION_DETECTED': {
              this.log(`New version detected: ${e.version?.hash}`);
              break;
            }
            case 'VERSION_READY': {
              this.log('New Version ready');
              break;
            }
            case 'VERSION_INSTALLATION_FAILED': {
              this.log(`Version installation failed: ${e.version?.hash}`, e.error);
              break;
            }
            case 'NO_NEW_VERSION_DETECTED': {
              this.log(`No new version detected`);
              break;
            }
          }
        }),
        filter((e) => e.type === 'VERSION_READY' && this.confirmReloadIntervalTimeout === null),
      )
      .subscribe(() => {
        this.promptUserForReload();

        this.confirmReloadIntervalTimeout = setInterval(() => {
          this.promptUserForReload();
        }, this.CONFIRMATION_RELOAD_INTERVAL);
      });
  }

  private subscribeToUnrecoverableState() {
    this.updates.unrecoverable.subscribe((e) => {
      this.log('Unrecoverable service worker state', e.reason);
    });
  }

  private promptUserForReload() {
    const USER_REJECTED_COUNT = this.getUserRejectedCount();

    const shouldReload = this.askUserIfAppCanBeReloaded(USER_REJECTED_COUNT);

    if (shouldReload) {
      this.resetUserRejectionCount();
      clearInterval(this.confirmReloadIntervalTimeout);
      this.confirmReloadIntervalTimeout = null;

      this.reloadPage();
    } else {
      this.incrementUserRejectionCount(USER_REJECTED_COUNT);
    }
  }

  private getUserRejectedCount(): number {
    return Number(this.storeService.getItem(this.USER_RELOAD_REJECT_COUNT_STORAGE_KEY) ?? '0');
  }

  private resetUserRejectionCount(): void {
    this.storeService.removeItem(this.USER_RELOAD_REJECT_COUNT_STORAGE_KEY);
  }

  private incrementUserRejectionCount(USER_REJECTED_COUNT: number): void {
    this.storeService.setItem(
      this.USER_RELOAD_REJECT_COUNT_STORAGE_KEY,
      String(USER_REJECTED_COUNT + 1),
    );
  }

  private askUserIfAppCanBeReloaded(USER_REJECTED_COUNT: number) {
    return USER_REJECTED_COUNT < this.USER_CAN_REJECT_UPDATE_MAX_COUNT
      ? window.confirm(this.translate.instant('appUpdate.newVersionReloadPrompt'))
      : true;
  }

  private reloadPage(): void {
    window.location.reload();
  }

  private executeUpdateVerification(): void {
    void this.updates.checkForUpdate().catch((reason) => {
      this.log(`Request failed.`, reason);
    });
  }
}
